import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SideMenu from '../components/SideMenu';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useEnvironment } from '../context/EnvironmentContext';
import { useIntegrations } from '../context/IntegrationsContext';
import './webhooks.css';

const EMPTY_FORM = {
  provider_code: '',
  environment: 'TEST',
  auth_type: 'API_KEY',
  credentials_summary_text: '{}',
};

const AUTH_TYPES = ['API_KEY', 'OAUTH2', 'BASIC', 'CUSTOM'];

const EMPTY_CREDENTIALS_FORM = {
  fields_text: '{}',
  secrets_ref: '',
  token_ref: '',
};

function formatFieldsPreview(fields) {
  if (!fields || typeof fields !== 'object') return '—';
  const keys = Object.keys(fields);
  if (keys.length === 0) return '—';
  if (keys.length <= 2) {
    return keys.map((k) => `${k}: ${fields[k]}`).join(', ');
  }
  return `${keys.length} fields`;
}

function formatUpdatedAt(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function credentialsToForm(credentials) {
  if (!credentials) return { ...EMPTY_CREDENTIALS_FORM };
  const fields = credentials.fields;
  return {
    fields_text:
      fields == null
        ? '{}'
        : typeof fields === 'string'
          ? fields
          : JSON.stringify(fields, null, 2),
    secrets_ref: credentials.secrets_ref ?? '',
    token_ref: credentials.token_ref ?? '',
  };
}

function resolveCompanyId(user) {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  if (roles.includes('administrator')) {
    return (
      user?.activeCompany?.id ??
      user?.activeCompany?.company_id ??
      user?.activeCompany?.companyId ??
      user?.myCompany?.id ??
      user?.myCompany?.company_id ??
      user?.myCompany?.companyId ??
      ''
    );
  }
  return (
    user?.myCompany?.id ??
    user?.myCompany?.company_id ??
    user?.myCompany?.companyId ??
    user?.company?.id ??
    ''
  );
}

function formatCredentialsSummary(summary) {
  if (!summary || typeof summary !== 'object') return '—';
  const keys = Object.keys(summary);
  if (keys.length === 0) return '—';
  if (keys.length <= 2) {
    return keys.map((k) => `${k}: ${summary[k]}`).join(', ');
  }
  return `${keys.length} fields`;
}

function accountToForm(account) {
  if (!account) return { ...EMPTY_FORM };
  const summary = account.credentials_summary;
  return {
    provider_code: account.provider_code || '',
    environment: account.environment || 'TEST',
    auth_type: account.auth_type || 'API_KEY',
    credentials_summary_text:
      summary == null
        ? '{}'
        : typeof summary === 'string'
          ? summary
          : JSON.stringify(summary, null, 2),
  };
}

export default function Integrations() {
  const location = useLocation();
  const { currentTheme, theme } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { environment } = useEnvironment();
  const {
    accounts,
    refreshAccounts,
    createAccount,
    updateAccount,
    deleteAccount,
    listCredentials,
    createCredentials,
    updateCredentials,
    deleteCredentials,
    loading,
    saving,
    error,
  } = useIntegrations();

  const [editing, setEditing] = useState(null);
  const [editMode, setEditMode] = useState('edit');
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [credentialsList, setCredentialsList] = useState([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [credentialsBusy, setCredentialsBusy] = useState(false);
  const [credentialsEditor, setCredentialsEditor] = useState(null);
  const [credentialsForm, setCredentialsForm] = useState({ ...EMPTY_CREDENTIALS_FORM });

  useEffect(() => {
    refreshAccounts({ environment });
  }, [refreshAccounts, environment]);

  const reloadCredentials = useCallback(async (accountId) => {
    if (!accountId) {
      setCredentialsList([]);
      return;
    }
    setCredentialsLoading(true);
    try {
      const list = await listCredentials(accountId);
      setCredentialsList(list);
    } finally {
      setCredentialsLoading(false);
    }
  }, [listCredentials]);

  useEffect(() => {
    if (!editing?.id || editMode !== 'edit') {
      setCredentialsList([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setCredentialsLoading(true);
      try {
        const list = await listCredentials(editing.id);
        if (!cancelled) setCredentialsList(list);
      } finally {
        if (!cancelled) setCredentialsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editing?.id, editMode, listCredentials]);

  const rows = useMemo(() => {
    return (Array.isArray(accounts) ? accounts : []).map((account, idx) => ({
      id: account?.id ?? `${idx}`,
      raw: account,
    }));
  }, [accounts]);

  const canSaveForm = useMemo(() => {
    return Boolean(form.provider_code.trim()) && Boolean(form.environment.trim());
  }, [form.provider_code, form.environment]);

  const openCreate = () => {
    setEditMode('create');
    setEditing({ id: null });
    setForm({ ...EMPTY_FORM, environment });
  };
  useEffect(() => { if (location.state?.quickCreate) openCreate(); }, [location.state]);

  const openEdit = (row) => {
    setEditMode('edit');
    setEditing(row);
    setForm(accountToForm(row.raw));
  };

  const closeEdit = () => {
    setEditing(null);
    setCredentialsEditor(null);
    setCredentialsList([]);
  };

  const openCredentialsCreate = () => {
    setCredentialsEditor({ mode: 'create' });
    setCredentialsForm({ ...EMPTY_CREDENTIALS_FORM });
  };

  const openCredentialsEdit = (credentials) => {
    setCredentialsEditor({ mode: 'edit', id: credentials.id, raw: credentials });
    setCredentialsForm(credentialsToForm(credentials));
  };

  const closeCredentialsEditor = () => {
    setCredentialsEditor(null);
  };

  const buildCredentialsPayload = () => {
    const trimmedFields = String(credentialsForm.fields_text || '').trim();
    let fields = {};
    if (trimmedFields) {
      fields = JSON.parse(trimmedFields);
    }
    return {
      fields,
      secrets_ref: credentialsForm.secrets_ref ?? '',
      token_ref: credentialsForm.token_ref ?? '',
    };
  };

  const onSaveCredentials = async () => {
    if (!editing?.id) return;
    try {
      const payload = buildCredentialsPayload();
      setCredentialsBusy(true);
      let res;
      if (credentialsEditor?.mode === 'create') {
        res = await createCredentials({
          courier_account_id: editing.id,
          ...payload,
        });
      } else if (credentialsEditor?.id) {
        res = await updateCredentials(credentialsEditor.id, payload);
      } else {
        return;
      }
      if (res?.ok) {
        closeCredentialsEditor();
        await reloadCredentials(editing.id);
      }
    } catch (err) {
      console.error('Invalid courier credentials form:', err);
      window.alert(t('integrationsInvalidJson'));
    } finally {
      setCredentialsBusy(false);
    }
  };

  const onRemoveCredentials = async () => {
    if (credentialsEditor?.mode !== 'edit' || !credentialsEditor?.id) return;
    if (!window.confirm(t('confirmRemoveCourierCredentials'))) return;
    setCredentialsBusy(true);
    try {
      const res = await deleteCredentials(credentialsEditor.id);
      if (res?.ok) {
        closeCredentialsEditor();
        await reloadCredentials(editing?.id);
      }
    } finally {
      setCredentialsBusy(false);
    }
  };

  const buildPayload = () => {
    const trimmedSummary = String(form.credentials_summary_text || '').trim();
    let credentials_summary = {};
    if (trimmedSummary) {
      credentials_summary = JSON.parse(trimmedSummary);
    }

    if (editMode === 'create') {
      return {
        provider_code: form.provider_code.trim().toUpperCase(),
        environment: (form.environment || 'TEST').trim().toUpperCase(),
        auth_type: form.auth_type.trim() || 'API_KEY',
        credentials_summary,
      };
    }

    return {
      provider_code: form.provider_code.trim().toUpperCase(),
      environment: (form.environment || 'TEST').trim().toUpperCase(),
      auth_type: form.auth_type.trim() || 'API_KEY',
      credentials_summary,
    };
  };

  const onSave = async () => {
    if (!canSaveForm) {
      window.alert(t('integrationsRequiredFields'));
      return;
    }

    try {
      const payload = buildPayload();
      if (editMode === 'create') {
        const companyId = resolveCompanyId(user);
        if (!companyId) {
          window.alert(t('integrationsMissingCompany'));
          return;
        }
        const res = await createAccount({ ...payload, company_id: companyId });
        if (res?.ok) {
          closeEdit();
          await refreshAccounts({ environment });
        }
        return;
      }

      const res = await updateAccount(editing.id, payload);
      if (res?.ok) {
        closeEdit();
        await refreshAccounts({ environment });
      }
    } catch (err) {
      console.error('Invalid integration form:', err);
      window.alert(t('integrationsInvalidJson'));
    }
  };

  const onRemove = async () => {
    if (editMode !== 'edit' || !editing?.id) return;
    if (!window.confirm(t('confirmRemoveIntegration'))) return;
    const res = await deleteAccount(editing.id);
    if (res?.ok) {
      closeEdit();
      await refreshAccounts({ environment });
    }
  };

  return (
    <div
      className="webhooks-page"
      style={{
        '--wh-background': currentTheme.background,
        '--wh-surface': currentTheme.surface,
        '--wh-border': currentTheme.border,
        '--wh-card-shadow': currentTheme.cardShadow,
        '--wh-text': currentTheme.text,
        '--wh-text-secondary': currentTheme.textSecondary,
      }}
    >
      <SideMenu currentTheme={currentTheme} theme={theme} />

      <main className="webhooks-main">
        <h1 className="webhooks-title">{t('integrations')}</h1>

        {loading && (
          <div className="webhooks-muted">{t('loading')}</div>
        )}

        {!loading && error && (
          <div className={`webhooks-error ${theme}`}>{t(error)}</div>
        )}

        {!loading && (
          <div className="webhooks-tableWrap">
            <div className={`webhooks-tableTopbar ${theme}`}>
              <div className="webhooks-tableTopbarTitle">{t('integrationsPanel')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  type="button"
                  onClick={openCreate}
                  disabled={saving}
                  className={[
                    'webhooks-primaryBtn',
                    theme,
                    saving ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {t('create')}
                </button>
                <button
                  type="button"
                  onClick={() => refreshAccounts({ environment })}
                  disabled={loading || saving}
                  className={[
                    'webhooks-secondaryBtn',
                    theme,
                    loading || saving ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {loading ? `${t('loading')}...` : t('refresh')}
                </button>
              </div>
            </div>

            {rows.length > 0 ? (
              <table className="webhooks-table">
                <thead>
                  <tr>
                    <th className="webhooks-th">{t('Provider')}</th>
                    <th className="webhooks-th">{t('pricingEnvironment')}</th>
                    <th className="webhooks-th">{t('integrationsAuthType')}</th>
                    <th className="webhooks-th">{t('integrationsCredentialsSummary')}</th>
                    <th className="webhooks-th webhooks-thRight">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const account = row.raw || {};
                    return (
                      <tr key={row.id} className={idx === rows.length - 1 ? 'webhooks-trLast' : ''}>
                        <td className="webhooks-td">{account.provider_code || '—'}</td>
                        <td className="webhooks-td">{account.environment || '—'}</td>
                        <td className="webhooks-td">{account.auth_type || '—'}</td>
                        <td className="webhooks-td">
                          {formatCredentialsSummary(account.credentials_summary)}
                        </td>
                        <td className="webhooks-td webhooks-actionsCell">
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            aria-label={t('integrationEditTitle')}
                            title={t('integrationEditTitle')}
                            className={['webhooks-iconBtn', theme].join(' ')}
                          >
                            <span className="oi oi-ellipses" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="webhooks-muted webhooks-tableEmpty">
                {t('integrationsEmpty')}
              </div>
            )}
          </div>
        )}

        {!!editing && (
          <div
            role="dialog"
            aria-modal="true"
            className={['webhooks-modalOverlay', theme].join(' ')}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeEdit();
            }}
          >
            <div className="webhooks-modal">
              <div className="webhooks-modalHeader">
                <div className="webhooks-modalTitle">
                  {editMode === 'create' ? t('integrationCreateTitle') : t('integrationEditTitle')}
                </div>
                <button
                  type="button"
                  onClick={closeEdit}
                  className={['webhooks-iconBtn', theme].join(' ')}
                  aria-label={t('Cancel')}
                  title={t('Cancel')}
                >
                  ×
                </button>
              </div>

              <div className="webhooks-modalBody">
                <div className="webhooks-formGrid">
                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('Provider')}</div>
                    <input
                      value={form.provider_code}
                      onChange={(e) => setForm((f) => ({ ...f, provider_code: e.target.value }))}
                      className={['webhooks-input', theme].join(' ')}
                      placeholder="e.g. DPD"
                      disabled={editMode === 'edit'}
                    />
                  </label>

                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('pricingEnvironment')}</div>
                    <select
                      value={form.environment}
                      onChange={(e) => setForm((f) => ({ ...f, environment: e.target.value }))}
                      className={['webhooks-select', theme].join(' ')}
                      disabled={editMode === 'edit'}
                      style={{
                        backgroundColor: currentTheme.surface,
                        color: currentTheme.text,
                        borderColor: currentTheme.border,
                        colorScheme: theme === 'dark' ? 'dark' : 'light',
                      }}
                    >
                      <option value="TEST">TEST</option>
                      <option value="PROD">PROD</option>
                    </select>
                  </label>

                  <label className="webhooks-label">
                    <div className="webhooks-fieldLabel">{t('integrationsAuthType')}</div>
                    <select
                      value={form.auth_type}
                      onChange={(e) => setForm((f) => ({ ...f, auth_type: e.target.value }))}
                      className={['webhooks-select', theme].join(' ')}
                      style={{
                        backgroundColor: currentTheme.surface,
                        color: currentTheme.text,
                        borderColor: currentTheme.border,
                        colorScheme: theme === 'dark' ? 'dark' : 'light',
                      }}
                    >
                      {AUTH_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="webhooks-label" style={{ gridColumn: '1 / -1' }}>
                    <div className="webhooks-fieldLabel">{t('integrationsCredentialsSummary')}</div>
                    <textarea
                      value={form.credentials_summary_text}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, credentials_summary_text: e.target.value }))
                      }
                      className={['webhooks-input', 'webhooks-textarea', theme].join(' ')}
                      rows={5}
                      placeholder="{}"
                    />
                  </label>

                  {editMode === 'edit' && (
                    <div style={{ gridColumn: '1 / -1', marginTop: 4 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                          marginBottom: 8,
                        }}
                      >
                        <div className="webhooks-fieldLabel" style={{ fontSize: 13, fontWeight: 700 }}>
                          {t('integrationsCourierCredentials')}
                        </div>
                        <button
                          type="button"
                          onClick={openCredentialsCreate}
                          disabled={saving || credentialsBusy}
                          className={[
                            'webhooks-secondaryBtn',
                            theme,
                            saving || credentialsBusy ? 'disabled' : 'enabled',
                          ].join(' ')}
                        >
                          {t('integrationsCredentialsAdd')}
                        </button>
                      </div>

                      {credentialsLoading && (
                        <div className="webhooks-muted">{t('integrationsCredentialsLoading')}</div>
                      )}

                      {!credentialsLoading && credentialsList.length === 0 && (
                        <div className="webhooks-muted">{t('integrationsCredentialsEmpty')}</div>
                      )}

                      {!credentialsLoading && credentialsList.length > 0 && (
                        <table className="webhooks-table" style={{ marginTop: 0 }}>
                          <thead>
                            <tr>
                              <th className="webhooks-th">{t('integrationsCredentialsUpdatedAt')}</th>
                              <th className="webhooks-th">{t('integrationsCredentialsFieldsPreview')}</th>
                              <th className="webhooks-th webhooks-thRight">{t('actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {credentialsList.map((cred, idx) => (
                              <tr
                                key={cred.id}
                                className={
                                  idx === credentialsList.length - 1 ? 'webhooks-trLast' : ''
                                }
                              >
                                <td className="webhooks-td">{formatUpdatedAt(cred.updated_at)}</td>
                                <td className="webhooks-td">{formatFieldsPreview(cred.fields)}</td>
                                <td className="webhooks-td webhooks-actionsCell">
                                  <button
                                    type="button"
                                    onClick={() => openCredentialsEdit(cred)}
                                    aria-label={t('integrationsCredentialsEditTitle')}
                                    title={t('integrationsCredentialsEditTitle')}
                                    className={['webhooks-iconBtn', theme].join(' ')}
                                  >
                                    <span className="oi oi-ellipses" aria-hidden="true" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="webhooks-modalFooter">
                {editMode === 'edit' && (
                  <button
                    type="button"
                    onClick={onRemove}
                    disabled={saving}
                    className={[
                      'webhooks-dangerBtn',
                      theme,
                      saving ? 'disabled' : 'enabled',
                    ].join(' ')}
                  >
                    {t('Delete')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={saving}
                  className={[
                    'webhooks-secondaryBtn',
                    theme,
                    saving ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={saving || !canSaveForm}
                  className={[
                    'webhooks-primaryBtn',
                    theme,
                    saving || !canSaveForm ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {saving ? t('Saving') : t('save')}
                </button>
              </div>
            </div>
          </div>
        )}

        {!!credentialsEditor && (
          <div
            role="dialog"
            aria-modal="true"
            className={['webhooks-modalOverlay', 'webhooks-modalOverlay--nested', theme].join(' ')}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeCredentialsEditor();
            }}
          >
            <div className="webhooks-modal">
              <div className="webhooks-modalHeader">
                <div className="webhooks-modalTitle">
                  {credentialsEditor.mode === 'create'
                    ? t('integrationsCredentialsCreateTitle')
                    : t('integrationsCredentialsEditTitle')}
                </div>
                <button
                  type="button"
                  onClick={closeCredentialsEditor}
                  className={['webhooks-iconBtn', theme].join(' ')}
                  aria-label={t('Cancel')}
                  title={t('Cancel')}
                >
                  ×
                </button>
              </div>

              <div className="webhooks-modalBody">
                <div className="webhooks-formGrid">
                  <label className="webhooks-label" style={{ gridColumn: '1 / -1' }}>
                    <div className="webhooks-fieldLabel">{t('integrationsCredentialsFields')}</div>
                    <textarea
                      value={credentialsForm.fields_text}
                      onChange={(e) =>
                        setCredentialsForm((f) => ({ ...f, fields_text: e.target.value }))
                      }
                      className={['webhooks-input', 'webhooks-textarea', theme].join(' ')}
                      rows={6}
                      placeholder="{}"
                    />
                  </label>

                  <label className="webhooks-label" style={{ gridColumn: '1 / -1' }}>
                    <div className="webhooks-fieldLabel">{t('integrationsCredentialsSecretsRef')}</div>
                    <textarea
                      value={credentialsForm.secrets_ref}
                      onChange={(e) =>
                        setCredentialsForm((f) => ({ ...f, secrets_ref: e.target.value }))
                      }
                      className={['webhooks-input', 'webhooks-textarea', theme].join(' ')}
                      rows={3}
                    />
                  </label>

                  <label className="webhooks-label" style={{ gridColumn: '1 / -1' }}>
                    <div className="webhooks-fieldLabel">{t('integrationsCredentialsTokenRef')}</div>
                    <textarea
                      value={credentialsForm.token_ref}
                      onChange={(e) =>
                        setCredentialsForm((f) => ({ ...f, token_ref: e.target.value }))
                      }
                      className={['webhooks-input', 'webhooks-textarea', theme].join(' ')}
                      rows={3}
                    />
                  </label>
                </div>
              </div>

              <div className="webhooks-modalFooter">
                {credentialsEditor.mode === 'edit' && (
                  <button
                    type="button"
                    onClick={onRemoveCredentials}
                    disabled={credentialsBusy}
                    className={[
                      'webhooks-dangerBtn',
                      theme,
                      credentialsBusy ? 'disabled' : 'enabled',
                    ].join(' ')}
                  >
                    {t('Delete')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeCredentialsEditor}
                  disabled={credentialsBusy}
                  className={[
                    'webhooks-secondaryBtn',
                    theme,
                    credentialsBusy ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="button"
                  onClick={onSaveCredentials}
                  disabled={credentialsBusy}
                  className={[
                    'webhooks-primaryBtn',
                    theme,
                    credentialsBusy ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {credentialsBusy ? t('Saving') : t('save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
